// eslint-disable-next-line
import React, { useState } from 'react';
import './Login.css';
import { Box, Typography } from '@mui/material';
import { toast } from 'react-hot-toast';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { Lock, Smartphone, Mail } from 'lucide-react';
import LogoImage from '../../../assets/NavbarIcon/logo-image.png';
import Cookies from 'js-cookie';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../../context/AuthContext';
import { useTranslation } from '../../../i18n/LanguageContext';

const Login = () => {
  const [auth, setAuth] = useAuth();
  const [userEmail, setUserEmail] = useState('');
  const [otpEmail, setOtpEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [validationEmailPassword, setValidationEmailPassword] = useState(false);
  const navigate = useNavigate();
  const { translate } = useTranslation();
  const SERVER_API_URL = process.env.REACT_APP_SERVER_API_URL;

  // Validation state
  const isEmailPasswordValid = userEmail.trim() !== '' && password.trim() !== '';
  const isOtpEmailValid = otpEmail.trim() !== '';

  const handleUserEmailChange = e => {
    setUserEmail(e.target.value);
    setValidationEmailPassword(false);
  };
  const handleUserPasswordChange = e => {
    setPassword(e.target.value);
    setValidationEmailPassword(false);
  };
  const handleOtpEmailChange = e => {
    setOtpEmail(e.target.value);
  };
  const handleClickShowPassword = () => {
    setShowPassword(prev => !prev);
  };
  const handleOtpLogin = async e => {
    if (e) e.preventDefault();

    const input = otpEmail.trim();
    if (!input) {
      toast.dismiss();
      toast.error('Please enter your email address or phone number');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const isEmail = emailRegex.test(input);

    // Prepare request body based on input type
    let requestBody;
    if (isEmail) {
      requestBody = { email: input.toLowerCase() };
    } else {
      // Simple phone validation for teacher frontend
      const phoneDigits = input.replace(/\D/g, '');
      if (phoneDigits.length < 10) {
        toast.dismiss();
        toast.error('Please enter a valid phone number');
        return;
      }
      requestBody = { phone_no: phoneDigits };
    }

    try {
      const response = await axios.post(`${SERVER_API_URL}/auth/otp-login`, requestBody, {
        withCredentials: true,
      });

      if (response.status === 200 && response.data) {
        toast.dismiss();
        toast.success('OTP sent successfully!');
        navigate('/verify-otp', { state: { email: input } });
      } else {
        toast.dismiss();
        toast.error(response.data.detail);
      }
    } catch (error) {
      console.error('Error sending OTP:', error);
      toast.dismiss();
      toast.error(error.response?.data?.detail || 'Failed to send OTP');
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();

    if (!userEmail.trim() || !password.trim()) {
      toast.dismiss();
      toast.error('Please enter your email and password');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!emailRegex.test(userEmail)) {
      toast.dismiss();
      toast.error(`${translate('login', 'validEmailText')}`);
      return;
    }

    try {
      const { data } = await axios.post(
        `${SERVER_API_URL}/auth/jwt/login`,
        {
          email: userEmail,
          password: password,
        },
        {
          withCredentials: true,
        }
      );
      if (data) {
        const { id, user_type, access_token, first_name, last_name, email, profile_image } = data;
        setAuth({
          ...auth,
          id,
          user_type,
          access_token,
          first_name,
          last_name,
          email,
          profile_image,
        });
        Cookies.set('access_token', access_token);
        Cookies.set('user_type', user_type);
        Cookies.set('first_name', first_name);
        Cookies.set('last_name', last_name);
        Cookies.set('email', email);
        Cookies.set('profile_image', profile_image);
        if (data?.user_type === 'teacher') {
          toast.dismiss();
          toast.success(`${translate('login', 'loginSuccessful')}`);
          navigate('/instructor/dashboard');
        } else {
          toast.dismiss();
          toast.error(`${translate('login', 'notAuthorised')}`);
        }
      }
    } catch (error) {
      console.log(error);
      setValidationEmailPassword(true);
    }
  };

  return (
    <Box className="login-container">
      {/* Header Section */}
      <Box className="login-header">
        <Box className="login-logo">
          <img src={LogoImage} alt="Klass Online Logo" className="logo-image" />
        </Box>
        <Typography variant="h3" className="welcome-title">
          Welcome Back, Instructor!
        </Typography>
        <Typography variant="body1" className="welcome-subtitle">
          Sign in to manage your sessions, connect with learners, and grow your teaching impact.
        </Typography>
      </Box>

      {/* Main Content - Two Cards */}
      <Box className="login-cards-container">
        {/* Left Card - Email & Password */}
        <Box className="login-card">
          <Box className="card-header">
            <Lock className="card-icon email-password-icon" />
            <Typography component="h3" className="card-title">
              Sign in with Email & Password
            </Typography>
          </Box>

          <form className="card-form" onSubmit={e => handleSubmit(e)}>
            <Box className="login-input-group">
              <label htmlFor="email" className="login-input-label">
                Email
              </label>
              <Box className="login-input-wrapper">
                <Mail className="login-input-icon" />
                <input
                  id="email"
                  className="login-input-field"
                  type="email"
                  placeholder="instructor@example.com"
                  value={userEmail}
                  onChange={handleUserEmailChange}
                  required
                />
              </Box>
            </Box>

            <Box className="login-input-group">
              <label htmlFor="password" className="login-input-label">
                Password
              </label>
              <Box className="login-input-wrapper">
                <Lock className="login-input-icon" />
                <input
                  id="password"
                  className="login-input-field"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter your password"
                  value={password}
                  onChange={handleUserPasswordChange}
                  required
                />
                <div
                  onClick={handleClickShowPassword}
                  className="login-password-toggle"
                  aria-label="toggle password visibility"
                >
                  {showPassword ? (
                    <VisibilityOff className="login-toggle-icon" />
                  ) : (
                    <Visibility className="login-toggle-icon" />
                  )}
                </div>
              </Box>
              {validationEmailPassword && (
                <div className="login-error-message">Incorrect Email or Password</div>
              )}
            </Box>

            <Box className="forgot-password-container">
              <Link to="/forgot-password" className="forgot-password-link">
                Forgot Password?
              </Link>
            </Box>

            <button
              type="submit"
              className={`primary-button ${!isEmailPasswordValid ? 'disabled' : ''}`}
              disabled={!isEmailPasswordValid}
            >
              Sign In
            </button>
          </form>
        </Box>

        {/* Right Card - OTP Login */}
        <Box className="login-card">
          <Box className="card-header">
            <Box className="otp-icon-container">
              <Smartphone className="card-icon otp-icon" />
            </Box>
            <Typography component="h3" className="card-title">
              Or Sign in with OTP
            </Typography>
          </Box>

          <Typography variant="body2" className="card-description">
            We'll send a one-time password to your registered email or phone number.
          </Typography>

          <form className="card-form" onSubmit={handleOtpLogin}>
            <Box className="login-input-group">
              <label htmlFor="otp-email" className="login-input-label">
                Email or Phone Number
              </label>
              <Box className="login-input-wrapper">
                <Mail className="login-input-icon" />
                <input
                  id="otp-email"
                  className="login-input-field"
                  type="text"
                  placeholder="Enter email or phone"
                  value={otpEmail}
                  onChange={handleOtpEmailChange}
                  required
                />
              </Box>
            </Box>

            <button
              type="submit"
              className={`primary-button ${!isOtpEmailValid ? 'disabled' : ''}`}
              disabled={!isOtpEmailValid}
            >
              Send OTP
            </button>
          </form>
        </Box>
      </Box>

      {/* Bottom Links */}
      <Box className="bottom-links">
        <Typography variant="body2" className="privacy-policy-text">
          <a
            href="https://klassonlinestorage.blob.core.windows.net/klassonlinestorage/Terms%20and%20Conditions/Privacy%20Policy%20Klass%20Online.pdf"
            target="_blank"
            rel="noopener noreferrer"
          >
            Privacy Policy
          </a>
        </Typography>
        <Typography variant="body2" className="privacy-policy-text">
          <a
            href="https://klassonlinestorage.blob.core.windows.net/klassonlinestorage/Terms%20and%20Conditions/Klass%20Online%20Terms%26Conditions.pdf"
            target="_blank"
            rel="noopener noreferrer"
          >
            Terms & Conditions
          </a>
        </Typography>
        <Typography variant="body2" className="privacy-policy-text">
          <a
            href="https://klassonlinestorage.blob.core.windows.net/klassonlinestorage/Terms%20and%20Conditions/Instructor%20Agreement.pdf"
            target="_blank"
            rel="noopener noreferrer"
          >
            Instructor Agreement
          </a>
        </Typography>
      </Box>
    </Box>
  );
};

export default Login;
